import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  AlarmDto,
  DeviceConfig,
  DisplayUnit,
  Port,
  SensorVisualizationType,
  TimeSeriesData,
} from '@thingslog/repositories';
import { Panel, PanelTitle } from './base';
import clsx from 'clsx';
import {
  AlarmSeverityChip,
  AlarmStatusChip,
  AlarmTypeChip,
  humanizeAlarmType,
  alarmTypeTranslationKey,
  CellContext,
  ColumnHelper,
  defineColumns,
  EverySelector,
  LocalizedDatePicker,
  TanstackRow,
  TanstackTable,
} from '@thingslog/ui-components';
import { addDays, differenceInDays, endOfDay, format, startOfDay, subDays } from 'date-fns';
import { DateUtil } from '@thingslog/utils';
import { useTranslation } from 'react-i18next';
import { getSensorUnitForVisualization } from '../utils';
import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

// Tanstack gives every column a default `size` of 150px, which the table's shrink-to-fit sizing
// treats as an explicit width. The alarm columns therefore state their own: date and status get
// just their content, description absorbs the rest.
const ALARM_DATE_COLUMN_WIDTH = 92;
const ALARM_STATUS_COLUMN_WIDTH = 104;
const ALARM_TYPE_COLUMN_WIDTH = 110;
const ALARM_DESCRIPTION_MIN_WIDTH = 150;

/** Tames MUI's default toggle buttons: no uppercasing, and compact enough for a panel header. */
const VIEW_TOGGLE_SX = {
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontSize: 12,
    lineHeight: 1.5,
    paddingY: '2px',
    paddingX: 1.25,
  },
};

/** Seconds are dropped — the year only earns its place when the rows straddle more than one. */
const ALARM_DATE_PATTERN = 'dd/MM HH:mm';
const ALARM_DATE_PATTERN_WITH_YEAR = 'dd/MM/yyyy HH:mm';

/**
 * Alarm timestamps arrive carrying the device's UTC offset (`2026-07-26T21:01:03+03:00`). The
 * wall-clock time as sent is what matters, so the offset is dropped instead of being displayed or
 * converted to the browser's timezone — same reading the values table gives.
 */
const formatAlarmDate = (value: string, pattern: string): string => {
  const date = new Date(value.replace(/(Z|[+-]\d{2}:\d{2})$/, ''));
  return Number.isNaN(date.getTime()) ? '' : format(date, pattern);
};

/**
 * Reads the year straight off the ISO string rather than parsing it — the offset the alarm carries
 * could otherwise tip a January or December timestamp into the neighbouring year.
 */
const spansMultipleYears = (alarms: AlarmDto[]): boolean => {
  const years = new Set(alarms.map((alarm: AlarmDto) => alarm.date?.slice(0, 4)).filter(Boolean));
  return years.size > 1;
};

/** Chronological sort for the ISO-string `date` column of the alarms view. */
const alarmDateSortingFn = (
  rowA: TanstackRow<AlarmDto>,
  rowB: TanstackRow<AlarmDto>,
  columnId: string
): number => {
  const a = new Date(rowA.getValue<string>(columnId)).getTime();
  const b = new Date(rowB.getValue<string>(columnId)).getTime();
  return (Number.isNaN(a) ? 0 : a) - (Number.isNaN(b) ? 0 : b);
};

export const DataTable: FC<DataTableProps> = ({
  selectedSensorIndex,
  data: tableData,
  className,
  ports,
  deviceConfig,
  everySelector,
  dateSelector,
  dataVisualizationType,
  maxDateRangeDays,
  isLoading,
  alarms,
  isAlarmsLoading,
  view = 'VALUES',
  onViewChange,
}: DataTableProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<TableRow[]>([]);
  const isAlarmsViewAvailable = alarms !== undefined;
  const isAlarmsView = isAlarmsViewAvailable && view === 'ALARMS';

  const columns = useMemo(
    () =>
      defineColumns<TableRow>((column: ColumnHelper<TableRow>) => [
        column.accessor('timestamp', {
          header: (): ReactNode => (
            <Typography variant="body2" className="text-gray-700 font-semibold">
              {t('data_table_date')}
            </Typography>
          ),
          cell: (info: CellContext<TableRow, number>): ReactNode => {
            const date = new Date(info.getValue());
            const formattedFull = DateUtil.formatTimeZoneAgnosticDate(date.toString());
            const formattedNoYear = format(date, 'dd/MM HH:mm:ss');
            return (
              <Typography variant="body2" className="text-gray-800">
                <span className="md:hidden">{formattedNoYear}</span>
                <span className="hidden md:inline">{formattedFull}</span>
              </Typography>
            );
          },
        }),
        column.accessor('value', {
          header: (): ReactNode => (
            <Typography variant="body2" className="text-gray-700 font-semibold">
              {t('data_table_value')}
            </Typography>
          ),
          cell: (info: CellContext<TableRow, number>): ReactNode => (
            <Typography variant="body2" className="text-gray-800">
              {info.getValue()} {info.row.original.unit}
            </Typography>
          ),
        }),
      ]),
    [t]
  );

  const alarmDatePattern = useMemo<string>(
    () => (spansMultipleYears(alarms ?? []) ? ALARM_DATE_PATTERN_WITH_YEAR : ALARM_DATE_PATTERN),
    [alarms]
  );

  // Device number, device name, sensor index and sensor name are omitted on purpose — the widget
  // always shows the alarms of the device and sensor already selected on the page.
  const alarmColumns = useMemo(
    () =>
      defineColumns<AlarmDto>((column: ColumnHelper<AlarmDto>) => [
        column.accessor('date', {
          header: (): ReactNode => (
            <Typography variant="body2" className="text-gray-700 font-semibold">
              {t('alarms_export_table_date')}
            </Typography>
          ),
          sortingFn: alarmDateSortingFn,
          // Date and status are pinned to roughly their content width so description keeps the
          // rest. The table lays out `auto`, so a longer translation still expands the column.
          size: ALARM_DATE_COLUMN_WIDTH,
          cell: (info: CellContext<AlarmDto, string>): ReactNode => {
            const value = info.getValue();
            if (!value) return null;
            return (
              <Typography variant="body2" className="whitespace-nowrap text-gray-800">
                {formatAlarmDate(value, alarmDatePattern)}
              </Typography>
            );
          },
        }),
        // Severity rides along with the status instead of taking a column of its own — the widget
        // is too narrow for four columns. The slot is always rendered, with a placeholder when the
        // alarm carries no severity, so the status chips stay aligned down the column.
        column.accessor('alarmStatusType', {
          header: (): ReactNode => (
            <Typography variant="body2" className="text-gray-700 font-semibold">
              {t('alarms_export_table_alarm_status')}
            </Typography>
          ),
          size: ALARM_STATUS_COLUMN_WIDTH,
          cell: (info: CellContext<AlarmDto, 'ACTIVE' | 'INACTIVE' | null>): ReactNode => {
            const status = info.getValue();
            const severity = info.row.original.severity;
            return (
              <div className="flex items-center gap-1">
                <AlarmSeverityChip
                  value={severity}
                  tooltip={
                    severity !== null && severity !== undefined
                      ? t('alarm_severity_tooltip', { value: severity })
                      : t('alarm_severity_none_tooltip')
                  }
                />
                {status !== null && (
                  <AlarmStatusChip
                    status={status}
                    label={
                      status === 'ACTIVE'
                        ? t('alarms_export_chip_status_active')
                        : t('alarms_export_chip_status_inactive')
                    }
                  />
                )}
              </div>
            );
          },
        }),
        column.accessor('alarmType', {
          header: (): ReactNode => (
            <Typography variant="body2" className="text-gray-700 font-semibold">
              {t('alarms_export_table_alarm_type')}
            </Typography>
          ),
          size: ALARM_TYPE_COLUMN_WIDTH,
          cell: (info: CellContext<AlarmDto, string>): ReactNode => {
            const alarmType = info.getValue();
            return (
              <AlarmTypeChip
                alarmType={alarmType}
                label={t(alarmTypeTranslationKey(alarmType), {
                  defaultValue: humanizeAlarmType(alarmType),
                })}
              />
            );
          },
        }),
        column.accessor('description', {
          header: (): ReactNode => (
            <Typography variant="body2" className="text-gray-700 font-semibold">
              {t('alarms_export_table_description')}
            </Typography>
          ),
          size: ALARM_DESCRIPTION_MIN_WIDTH,
          meta: { grow: true },
          cell: (info: CellContext<AlarmDto, string>): ReactNode => (
            <Typography variant="body2" className="text-gray-800">
              {info.getValue()}
            </Typography>
          ),
        }),
      ]),
    [t, alarmDatePattern]
  );

  useEffect(() => {
    if (selectedSensorIndex === null) {
      setData([]);
      return;
    }

    let unit: string = DisplayUnit.NO_UNITS;
    if (ports !== undefined && selectedSensorIndex !== null) {
      try {
        let sensor = ports[selectedSensorIndex].sensor;
        unit = getSensorUnitForVisualization(sensor, dataVisualizationType);
      } catch {
        unit = DisplayUnit.NO_UNITS;
      }
    }

    setData(tableData.map((row: TableRow) => ({ ...row, unit })));
  }, [selectedSensorIndex, tableData, ports, dataVisualizationType]);

  return (
    <Panel className={clsx('space-y-2.5', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PanelTitle title={t('data_table_widget_header')} />
        {isAlarmsViewAvailable && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={isAlarmsView ? 'ALARMS' : 'VALUES'}
            // An exclusive group hands back null when the active button is clicked again; the
            // widget always shows one of the two tables, so that press is a no-op.
            onChange={(_: React.MouseEvent<HTMLElement>, next: DataTableView | null): void => {
              if (next !== null) onViewChange?.(next);
            }}
            sx={VIEW_TOGGLE_SX}
          >
            <ToggleButton value="VALUES">{t('data_table_measurements')}</ToggleButton>
            <ToggleButton value="ALARMS">
              {t('data_table_alarms')}
              {/* Red only when there is something to look at; an empty period stays neutral. */}
              <span
                className={clsx(
                  'ml-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1',
                  'text-[11px] font-semibold leading-none tabular-nums',
                  (alarms?.length ?? 0) > 0 ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500'
                )}
              >
                {alarms?.length ?? 0}
              </span>
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </div>
      {dateSelector !== undefined && (
        <div className="flex flex-wrap items-center gap-2">
          <LocalizedDatePicker
            value={dateSelector.fromDate}
            minDate={
              maxDateRangeDays != null ? subDays(dateSelector.toDate, maxDateRangeDays) : undefined
            }
            maxDate={dateSelector.toDate}
            onChange={(newDate: Date | null): void => {
              if (!newDate) return;
              const newFrom = startOfDay(newDate);
              if (maxDateRangeDays != null) {
                const currentTo = dateSelector.toDate;
                const rangeDays = differenceInDays(currentTo, newFrom);
                if (rangeDays > maxDateRangeDays) {
                  const adjustedTo = endOfDay(addDays(newFrom, maxDateRangeDays));
                  dateSelector.onDateChange(newFrom, 'FROM');
                  dateSelector.onDateChange(adjustedTo, 'TO');
                  return;
                }
              }
              dateSelector.onDateChange(newFrom, 'FROM');
            }}
            inputFormat="dd/MM/yyyy"
            renderInputTextField={{ size: 'small' }}
          />
          <span className="text-sm">-</span>
          <LocalizedDatePicker
            value={dateSelector.toDate}
            minDate={dateSelector.fromDate}
            maxDate={
              maxDateRangeDays != null
                ? addDays(dateSelector.fromDate, maxDateRangeDays)
                : undefined
            }
            onChange={(newDate: Date | null): void => {
              if (!newDate) return;
              const newTo = endOfDay(newDate);
              if (maxDateRangeDays != null) {
                const currentFrom = dateSelector.fromDate;
                const rangeDays = differenceInDays(newTo, currentFrom);
                if (rangeDays > maxDateRangeDays) {
                  const adjustedTo = endOfDay(addDays(currentFrom, maxDateRangeDays));
                  dateSelector.onDateChange(adjustedTo, 'TO');
                  return;
                }
              }
              dateSelector.onDateChange(newTo, 'TO');
            }}
            inputFormat="dd/MM/yyyy"
            renderInputTextField={{ size: 'small' }}
          />
        </div>
      )}
      {!isAlarmsView &&
        deviceConfig &&
        everySelector !== undefined &&
        everySelector.isVisible === true && (
          <div>
            <EverySelector
              confEvery={deviceConfig.every}
              confPeriod={deviceConfig.recordPeriod}
              every={everySelector.every}
              onEveryChange={everySelector.onEveryChange}
              translation={{
                day: t('day'),
                days: t('days'),
                hour: t('hour'),
                hours: t('hours'),
                minute: t('minute'),
                minutes: t('minutes'),
                second: t('second'),
                seconds: t('seconds'),
                label: t('every'),
              }}
            />
          </div>
        )}
      {/*
       * The two tables share a slot, so without distinct keys React reuses one Tanstack instance
       * across the switch and its sorting/pagination state leaks onto columns that do not exist in
       * the other view.
       */}
      <section>
        {isAlarmsView ? (
          <TanstackTable<AlarmDto>
            key="alarms"
            mode="client"
            data={alarms ?? []}
            columns={alarmColumns}
            getRowId={(row: AlarmDto, index: number): string =>
              `${row.date}-${row.alarmType}-${index}`
            }
            isLoading={isAlarmsLoading}
            showToolbar={false}
            enableColumnFilters={false}
            density="compact"
            pageSizeOptions={[5, 10, 15, 25, 50]}
            initialState={{
              sorting: [{ id: 'date', desc: true }],
              pagination: { pageIndex: 0, pageSize: 10 },
            }}
            emptyMessage={t('alarms_none_message')}
            classNames={{ container: 'max-h-[22rem]' }}
          />
        ) : (
          <TanstackTable<TableRow>
            key="values"
            mode="client"
            data={data}
            columns={columns}
            getRowId={(row: TableRow): string => `${row.timestamp}-${row.value}`}
            isLoading={isLoading}
            showToolbar={false}
            enableColumnFilters={false}
            density="compact"
            pageSizeOptions={[5, 10, 15, 25, 50]}
            initialState={{
              sorting: [{ id: 'timestamp', desc: true }],
              pagination: { pageIndex: 0, pageSize: 10 },
            }}
            emptyMessage={t('no_data_for_selected_period')}
            classNames={{ container: 'max-h-[22rem]' }}
          />
        )}
      </section>
    </Panel>
  );
};

interface TableRow extends TimeSeriesData<number> {
  unit?: string;
}

export type DataTableView = 'VALUES' | 'ALARMS';

interface DataTableProps {
  selectedSensorIndex: number | null;
  data: TimeSeriesData<number>[]; // table data (flow, readings, or alarms)
  className?: string;
  dateSelector?: {
    fromDate: Date;
    toDate: Date;
    onDateChange: (toDate: Date, source: 'FROM' | 'TO') => void;
  };
  ports?: Record<number, Port>;
  deviceConfig?: DeviceConfig;
  everySelector?: {
    isVisible: boolean;
    every: number;
    onEveryChange: (every: number) => void;
  };
  dataVisualizationType: SensorVisualizationType | null;
  /**
   * Maximum allowed period in days between fromDate and toDate. If set, the date range
   * cannot exceed this many days. When null/undefined, no limit is applied.
   */
  maxDateRangeDays?: number | null;
  /** Shows the table's loading bar while the underlying readings/flow query is fetching. */
  isLoading?: boolean;
  /**
   * Alarms of the selected device/sensor for the same period as the values. When undefined the
   * values/alarms switch is hidden altogether.
   */
  alarms?: AlarmDto[];
  /** Shows the table's loading bar while the alarms query is fetching. */
  isAlarmsLoading?: boolean;
  /** Which table is shown. Controlled by the page so it can be persisted in the URL. */
  view?: DataTableView;
  onViewChange?: (view: DataTableView) => void;
}
